import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/transforms';
import '../../pdf/draw/cut';

/**
 * Losovani (Lottery Wheel) - Pie-chart lottery wheel
 *
 * Layout: 2 portrait pages.
 * Page 1: wheel with images + text labels. Page 2: wheel with images only.
 *
 * Migrated from C# EduFrame.Img10.Generators.Losovani (LabelText version)
 */
export class KoloLosovani implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 4;
  }
  countMax(): number {
    return 16;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const sz = PdfSize.A4.width - 2 * margin;
    const rect = new PdfRect(margin, (PdfSize.A4.height - sz) / 2, sz, sz);

    const count = Math.min(opts.list.length, this.countMax());
    const alpha = Math.PI / count;

    const r_img = sz * 0.48;
    const maxImgSize = sz * 0.2;
    const imgSize = Math.min(maxImgSize, (r_img * Math.tan(alpha)) / (0.5 + Math.tan(alpha)));

    const r_text = r_img - imgSize - sz * 0.01;
    const textHeight = imgSize / 3;
    const textSize = 2 * (r_text - textHeight) * Math.tan(alpha);

    const brushes = [
      new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1)),
      new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.2)),
    ];

    const allTexts = opts.list.map((t) => t.text);
    const labelTexts = opts.list.slice(0, count).map((t) => [t.label.getText() ?? '']);
    const page1 = pdf.addPage();
    const page2 = pdf.addPage();
    const f = page1.adjustFont(textSize, allTexts, GUtil.baseFont);
    const labelFont = page1.adjustFontMulti(
      labelTexts,
      GUtil.baseFont,
      new PdfSize(imgSize, imgSize)
    );

    // Draw pie slices (background)
    for (let i = 0; i < count; i++) {
      const angle = (360 / count) * i;

      page1.save();
      page1.rotateAtTransform(angle, new PdfPoint(rect.centerX, rect.centerY));
      page1.drawPie(GUtil.basePen, brushes[i % brushes.length], rect, 0, 360 / count);
      page1.restore();

      page2.save();
      page2.rotateAtTransform(angle, new PdfPoint(rect.centerX, rect.centerY));
      page2.drawPie(GUtil.basePen, brushes[i % brushes.length], rect, 0, 360 / count);
      page2.restore();
    }

    // Draw images and text
    for (let i = 0; i < count; i++) {
      const angle = (360 / count) * (i + 0.5) + 90;

      const imgRect = new PdfRect(
        rect.centerX - imgSize / 2,
        rect.centerY - r_img,
        imgSize,
        imgSize
      );
      const textRect = new PdfRect(
        rect.centerX - textSize / 2,
        rect.centerY - r_text,
        textSize,
        textHeight
      );

      page1.save();
      page1.rotateAtTransform(angle, new PdfPoint(rect.centerX, rect.centerY));
      page1.drawLabel(opts.list[i].label, { rect: imgRect, font: labelFont });
      page1.drawString(opts.list[i].text, f, PdfBrush.Black, null, textRect, ETextAlign.Center);
      page1.restore();

      page2.save();
      page2.rotateAtTransform(angle, new PdfPoint(rect.centerX, rect.centerY));
      page2.drawLabel(opts.list[i].label, { rect: imgRect, font: labelFont });
      page2.restore();
    }

    page1.drawEllipse(GUtil.basePen, null, rect);
    page2.drawEllipse(GUtil.basePen, null, rect);
    page1.cutCircle(new PdfPoint(rect.centerX, rect.centerY));
    page2.cutCircle(new PdfPoint(rect.centerX, rect.centerY));
  }
}
